import React, { useMemo, useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import * as XLSX from "xlsx";

import styles from "./OrdersTable.module.css";

import CountryFlag from "../../components/CountryFlag";
import Loading from "../../components/Loading";
import DeleteButton from "../../components/buttons/DeleteButton";
import AddNewButton from "../../components/buttons/AddNewButton";
import CancelButton from "../../components/buttons/CancelButton";
import SaveButton from "../../components/buttons/SaveButton";
import PrintButton from "../../components/buttons/PrintButton";
import OrderFormFields from "./OrderFormFields";

import { componentOrderService as orderService, countryService } from "../../services/api";
import toShort from "../../utils/dateParser";
import { toDateInputValue, validateOrderDateChain } from "../../utils/orderDates";

const FILTER_CONFIGS = [
  { key: "partNumber", label: "Part Number", code: "PN" },
  { key: "dateInMonth", label: "Date", code: "DT" },
  { key: "customerName", label: "Customer", code: "CU" },
  { key: "status", label: "Status", code: "ST" },
  { key: "euIndicator", label: "EU", code: "EU" },
];

const DEFAULT_FILTERS = {
  partNumber: [],
  dateInMonth: [],
  customerName: [],
  status: [],
  euIndicator: [],
};

const STATUS_ORDER = ["ordered", "progress", "released", "delivered", "cancelled"];

const hasEuCountry = (order) =>
  order.originCountryEuStatus === "EU" || order.destinationCountryEuStatus === "EU";

const hasMissingRouteCountry = (order) => !order.originCountryId || !order.destinationCountryId;

const isLocalBulgarianRoute = (order) =>
  order.originCountryCode === "BG" && order.destinationCountryCode === "BG";

const isNonEuRoute = (order) =>
  !hasMissingRouteCountry(order) &&
  !hasEuCountry(order) &&
  (order.originCountryEuStatus === "NON_EU" || order.destinationCountryEuStatus === "NON_EU");

const formatEuIndicator = (order) => {
  if (hasMissingRouteCountry(order)) {
    return "?";
  }
  if (isLocalBulgarianRoute(order)) {
    return "BG";
  }
  if (isNonEuRoute(order)) {
    return "World";
  }
  return hasEuCountry(order) ? "EU" : "";
};

const formatCountry = (flag, name, code) => {
  if (!name) {
    return "";
  }
  return `${flag ? `${flag} ` : ""}${name}${code ? ` (${code})` : ""}`;
};

const formatMonthBucket = (value) => {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
};

const getFilterValue = (order, key) => {
  if (key === "dateInMonth") {
    return formatMonthBucket(order.dateIn);
  }
  if (key === "euIndicator") {
    if (hasMissingRouteCountry(order)) {
      return "Missing route";
    }
    if (isLocalBulgarianRoute(order)) {
      return "Local BG";
    }
    return hasEuCountry(order) ? "EU" : "Non-EU";
  }
  return (order[key] ?? "").toString().trim();
};

const matchesFilters = (order, filters, excludedKey = null) =>
  FILTER_CONFIGS.every(({ key }) => {
    if (key === excludedKey) {
      return true;
    }
    const selectedValues = filters[key] || [];
    if (!selectedValues.length) {
      return true;
    }
    const rowValue = getFilterValue(order, key);
    return selectedValues.includes(rowValue);
  });

const OrdersTable = ({ reportType = "recent" }) => {
  const [orders, setOrders] = useState([]);
  const [countries, setCountries] = useState([]);
  const [editingOrder, setEditingOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [openFilterKey, setOpenFilterKey] = useState(null);
  const [filterSearch, setFilterSearch] = useState({});
  const filtersFrameRef = useRef(null);
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  // Function to fetch orders from the database
  const fetchOrders = async () => {
    try {
      setLoading(true);
      setError(null);

      const ordersRequest = reportType === "recent" ? orderService.getRecent() : orderService.getAll();
      const [ordersResponse, countriesResponse] = await Promise.all([
        ordersRequest,
        countryService.getAll(),
      ]);

      setOrders(ordersResponse.data.data);
      setCountries(countriesResponse.data.data || []);
    } catch (err) {
      console.error("Error fetching orders:", err);
      setError("Failed to load orders");
    } finally {
      setLoading(false);
    }
  };

  // Fetch orders on component mount and when reportType changes
  useEffect(() => {
    fetchOrders();
  }, [reportType]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!openFilterKey) {
      return undefined;
    }

    const handleOutsideClick = (event) => {
      const frame = filtersFrameRef.current;
      if (!frame) {
        return;
      }

      const eventPath = typeof event.composedPath === "function" ? event.composedPath() : [];
      const isInsideFilters = eventPath.length
        ? eventPath.includes(frame)
        : frame.contains(event.target);

      if (!isInsideFilters) {
        setOpenFilterKey(null);
      }
    };

    document.addEventListener("pointerdown", handleOutsideClick, true);
    return () => {
      document.removeEventListener("pointerdown", handleOutsideClick, true);
    };
  }, [openFilterKey]);

  const defineNewOrderNumber = () => {
    const maxOrder = orders.reduce((max, order) => {
      const orderNum = parseInt(order.internalNo.replace(/\D/g, ""));
      return orderNum > max ? orderNum : max;
    }, 0);

    return `O-${String(maxOrder + 1).padStart(3, "0")}`;
  };

  const handleAddNew = () => {
    const newOrder = {
      id: Date.now(), // Temporary ID
      internalNo: defineNewOrderNumber(),
      componentName: "",
      partNumber: "",
      serialNumber: "",
      customerName: "",
      status: "ordered",
      dateIn: toDateInputValue(new Date()),
      workStarted: "",
      workCompleted: "",
      dateOut: "",
      originCountryId: "",
      destinationCountryId: "",
      reason: "",
      remarks: "",
    };
    setEditingOrder(newOrder);
  };

  const handleSave = async () => {
    try {
      // Validate required fields
      if (
        !editingOrder.internalNo ||
        !editingOrder.partNumber ||
        !editingOrder.serialNumber ||
        !editingOrder.customerName
      ) {
        setError("Please fill in all required fields");
        return;
      }

      setError(null);

      const dateError = validateOrderDateChain(editingOrder);
      if (dateError) {
        setError(dateError);
        return;
      }

      await orderService.create(editingOrder);
      await fetchOrders();

      setEditingOrder(null);
    } catch (err) {
      console.error("Error saving order:", err);
      setError(err.response?.data?.message || "Failed to save order");
    }
  };

  const handleCancel = () => {
    setEditingOrder(null);
    setError(null);
  };

  const handleDelete = async (orderId) => {
    if (window.confirm("Are you sure you want to delete this order?")) {
      try {
        await orderService.delete(orderId);
        // Refresh data from database
        await fetchOrders();
      } catch (err) {
        console.error("Error deleting order:", err);
        setError(err.response?.data?.message || "Failed to delete order");
      }
    }
  };

  const handleInputChange = (field, value) => {
    if (
      field === "dateIn" ||
      field === "dateOut" ||
      field === "workStarted" ||
      field === "workCompleted"
    ) {
      value = toDateInputValue(value);
    }
    setEditingOrder((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const toggleFilterValue = (filterKey, value) => {
    setFilters((prev) => {
      const existingValues = prev[filterKey];
      const nextValues = existingValues.includes(value)
        ? existingValues.filter((item) => item !== value)
        : [...existingValues, value];
      return {
        ...prev,
        [filterKey]: nextValues,
      };
    });
  };

  const clearFilter = (filterKey) => {
    setFilters((prev) => ({
      ...prev,
      [filterKey]: [],
    }));
    setFilterSearch((prev) => ({
      ...prev,
      [filterKey]: "",
    }));
  };

  const clearAllFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setFilterSearch({});
  };

  const hasActiveFilters = useMemo(
    () => FILTER_CONFIGS.some(({ key }) => (filters[key] || []).length > 0),
    [filters]
  );

  const filterOptions = useMemo(() => {
    const options = {};
    FILTER_CONFIGS.forEach(({ key }) => {
      const values = new Set();
      orders.forEach((order) => {
        const value = getFilterValue(order, key);
        if (value) {
          values.add(value);
        }
      });
      const optionValues = [...values];
      if (key === "euIndicator") {
        const euOrder = ["EU", "Local BG", "Non-EU", "Missing route"];
        optionValues.sort((a, b) => euOrder.indexOf(a) - euOrder.indexOf(b));
      } else if (key === "status") {
        optionValues.sort((a, b) => {
          const indexA = STATUS_ORDER.indexOf(a);
          const indexB = STATUS_ORDER.indexOf(b);
          if (indexA !== -1 && indexB !== -1) {
            return indexA - indexB;
          }
          return a.localeCompare(b);
        });
      } else {
        optionValues.sort((a, b) => a.localeCompare(b));
      }
      options[key] = optionValues;
    });
    return options;
  }, [orders]);

  const facetCounts = useMemo(() => {
    const countsByFilter = {};
    FILTER_CONFIGS.forEach(({ key }) => {
      const counts = {};
      orders
        .filter((order) => matchesFilters(order, filters, key))
        .forEach((order) => {
          const value = getFilterValue(order, key);
          if (!value) {
            return;
          }
          counts[value] = (counts[value] || 0) + 1;
        });
      countsByFilter[key] = counts;
    });
    return countsByFilter;
  }, [orders, filters]);

  const filteredOrders = useMemo(
    () => orders.filter((order) => matchesFilters(order, filters)),
    [orders, filters]
  );

  const duplicateInternalNos = useMemo(() => {
    const counts = new Map();
    for (const order of orders) {
      const key = (order.internalNo ?? "").toString().trim();
      if (!key) {
        continue;
      }
      counts.set(key, (counts.get(key) || 0) + 1);
    }
    const dups = new Set();
    for (const [key, count] of counts) {
      if (count > 1) {
        dups.add(key);
      }
    }
    return dups;
  }, [orders]);

  const buildExportFileName = () => {
    const activeCodes = FILTER_CONFIGS.filter(({ key }) => filters[key].length > 0).map(
      ({ code }) => code
    );
    const filterSegment = activeCodes.length ? activeCodes.join("-") : "All";
    const now = new Date();
    const stamp = [
      now.getFullYear(),
      String(now.getMonth() + 1).padStart(2, "0"),
      String(now.getDate()).padStart(2, "0"),
      String(now.getHours()).padStart(2, "0"),
      String(now.getMinutes()).padStart(2, "0"),
      String(now.getSeconds()).padStart(2, "0"),
    ];
    return `Orders_${filterSegment}_${stamp[0]}-${stamp[1]}-${stamp[2]}-${stamp[3]}-${stamp[4]}-${stamp[5]}.xlsx`;
  };

  const handleExportXlsx = () => {
    const rowsForExport = filteredOrders.map((order) => ({
      "Date In": toShort(order.dateIn),
      "Work started": toShort(order.workStarted),
      "Work completed": toShort(order.workCompleted),
      "Int #": order.internalNo || "",
      Component: order.componentName || "",
      "Part Number": order.partNumber || "",
      "Serial Number": order.serialNumber || "",
      Reason: order.reason || "",
      Customer: order.customerName || "",
      Origin: formatCountry(order.originCountryFlag, order.originCountryName, order.originCountryCode),
      Destination: formatCountry(
        order.destinationCountryFlag,
        order.destinationCountryName,
        order.destinationCountryCode
      ),
      Status: order.status || "",
      "Date Out": toShort(order.dateOut),
      Remarks: order.remarks || "",
      EU: formatEuIndicator(order),
      Inv: Number(order.invoiceCount) > 0 ? "" : "❌",
      "Created By": order.createdBy || "",
      "Updated By": order.updatedBy || "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(rowsForExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Orders");
    XLSX.writeFile(workbook, buildExportFileName());
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "ordered":
        return styles.statusOrdered;
      case "progress":
        return styles.statusProgress;
      case "released":
        return styles.statusReleased;
      case "delivered":
        return styles.statusDelivered;
      case "cancelled":
        return styles.statusCancelled;
      default:
        return styles.statusProgress;
    }
  };

  // Refresh button handler
  const handleRefresh = () => {
    fetchOrders();
  };

  if (loading) {
    return <Loading loadingText="Loading orders..." />;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerActions}>
          <button
            onClick={handleRefresh}
            className={styles.refreshButton}
            title="Refresh data"
            disabled={loading}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
          {hasPermission("component_orders:create") && (
            <AddNewButton text="+ Add New Order" onClick={handleAddNew} disabled={!!editingOrder} />
          )}
        </div>
      </div>

      {error && (
        <div className={styles.error}>
          {error}
          <button 
            onClick={() => setError(null)} 
            className={styles.dismissError}
          >
            ×
          </button>
        </div>
      )}

      {editingOrder && (
        <div className={styles.editForm}>
          <h3 className={styles.editTitle}>Add New Order</h3>

          <OrderFormFields order={editingOrder} countries={countries} onChange={handleInputChange} />

          <div className={styles.formActions}>
            <SaveButton text="Create Order" onClick={handleSave} />
            <CancelButton onClick={handleCancel} />
          </div>
        </div>
      )}

      <div className={styles.filtersFrame} ref={filtersFrameRef}>
        <p className={styles.filtersLabel}>Filters</p>
        <div className={styles.filtersBar}>
          {FILTER_CONFIGS.map(({ key, label }) => {
            const selectedValues = filters[key] || [];
            const options = filterOptions[key] || [];
            const searchText = (filterSearch[key] || "").toLowerCase();
            const visibleOptions = options.filter((option) =>
              option.toLowerCase().includes(searchText)
            );
          const counts = facetCounts[key] || {};
          const isOpen = openFilterKey === key;
          return (
            <div key={key} className={styles.filterDropdown}>
              <button
                type="button"
                className={`${styles.filterTrigger} ${isOpen ? styles.filterTriggerOpen : ""}`}
                onClick={() => setOpenFilterKey(isOpen ? null : key)}
              >
                {label}
                {selectedValues.length > 0 && (
                  <span className={styles.selectedBadge}>{selectedValues.length}</span>
                )}
              </button>
              {isOpen && (
                <div className={styles.dropdownPanel}>
                  <input
                    type="text"
                    className={styles.filterSearchInput}
                    placeholder="Search options..."
                    value={filterSearch[key] || ""}
                    onChange={(event) =>
                      setFilterSearch((prev) => ({
                        ...prev,
                        [key]: event.target.value,
                      }))
                    }
                  />
                  <div className={styles.dropdownOptions}>
                    {visibleOptions.length === 0 ? (
                      <div className={styles.emptyOption}>No options</div>
                    ) : (
                      visibleOptions.map((option) => (
                        <label key={option} className={styles.optionItem}>
                          <input
                            type="checkbox"
                            checked={selectedValues.includes(option)}
                            onChange={() => toggleFilterValue(key, option)}
                          />
                          <span>{option}</span>
                          <span className={styles.optionCount}>[{counts[option] || 0}]</span>
                        </label>
                      ))
                    )}
                  </div>
                  <button
                    type="button"
                    className={styles.clearFilterButton}
                    onClick={() => clearFilter(key)}
                    disabled={!selectedValues.length}
                  >
                    Clear filter
                  </button>
                </div>
              )}
            </div>
          );
          })}
          <div className={styles.filtersActions}>
            <button
              type="button"
              className={styles.secondaryActionButton}
              onClick={clearAllFilters}
              disabled={!hasActiveFilters}
            >
              Clear all filters
            </button>
            <button
              type="button"
              className={styles.exportButton}
              onClick={handleExportXlsx}
              disabled={filteredOrders.length === 0}
            >
              Export .xlsx
            </button>
          </div>
        </div>
      </div>

      {/* Orders Table */}
      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead className={styles.tableHeader}>
            <tr>
              <th>Date In</th>
              <th>Int #</th>
              <th>Component</th>
              <th>Part Number</th>
              <th>Serial Number</th>
              <th>Reason</th>
              <th>Customer</th>
              <th>Status</th>
              <th>Date Out</th>
              <th>Remarks</th>
              <th>EU</th>
              <th>Inv</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredOrders.length === 0 ? (
              <tr>
                <td colSpan="13" className={styles.noData}>
                  {orders.length === 0
                    ? `No orders found. ${
                        hasPermission("component_orders:create")
                          ? "Click 'Add New Order' to create one."
                          : ""
                      }`
                    : "No orders match the selected filters."}
                </td>
              </tr>
            ) : (
              filteredOrders.map((order) => {
                const internalNoKey = (order.internalNo ?? "").toString().trim();
                const isDuplicateInt =
                  internalNoKey.length > 0 && duplicateInternalNos.has(internalNoKey);
                return (
                <tr
                  key={order.id}
                  className={`${styles.tableRow} ${styles.clickableRow}`}
                  onClick={() => navigate(`/orders/${order.id}`)}
                >
                  <td className={styles.date}>{toShort(order.dateIn)}</td>
                  <td className={styles.orderNumberCell}>
                    <span
                      className={`${styles.orderNumber}${
                        isDuplicateInt ? ` ${styles.orderNumberDuplicate}` : ""
                      }`}
                      title={
                        isDuplicateInt
                          ? "This internal number is used on more than one order"
                          : undefined
                      }
                    >
                      {order.internalNo}
                    </span>
                  </td>
                  <td className={styles.componentName}>{order.componentName}</td>
                  <td className={styles.quantity}>{order.partNumber}</td>
                  <td className={styles.supplier}>{order.serialNumber}</td>
                  <td className={styles.notes}>{order.reason}</td>
                  <td className={styles.supplier}>{order.customerName}</td>
                  <td>
                    <span className={`${styles.statusBadge} ${getStatusBadgeClass(order.status)}`}>
                      {order.status?.charAt(0).toUpperCase() + order.status?.slice(1)}
                    </span>
                  </td>
                  <td className={styles.date}>{toShort(order.dateOut)}</td>
                  <td className={styles.notes}>{order.remarks}</td>
                  <td className={styles.euIndicator}>
                    {hasMissingRouteCountry(order) ? (
                      <span className={styles.missingFlag} title="Origin or destination not selected">
                        ?
                      </span>
                    ) : isLocalBulgarianRoute(order) ? (
                      <CountryFlag code="BG" label="Bulgaria flag" className={styles.flagIcon} />
                    ) : isNonEuRoute(order) ? (
                      <span className={styles.globeIndicator} title="Non-EU route" aria-label="Non-EU route">
                        🌍
                      </span>
                    ) : hasEuCountry(order) ? (
                      <CountryFlag code="EU" label="European Union flag" className={styles.flagIcon} />
                    ) : null}
                  </td>
                  <td
                    className={styles.invIndicator}
                    aria-label={
                      Number(order.invoiceCount) > 0 ? "At least one invoice issued" : "No invoices issued"
                    }
                  >
                    {Number(order.invoiceCount) > 0 ? null : (
                      <span title="No invoices issued">❌</span>
                    )}
                  </td>
                  <td className={styles.actions} onClick={(e) => e.stopPropagation()}>
                    <PrintButton data={order} />
                    {hasPermission("component_orders:delete") && (
                      <DeleteButton onClick={() => handleDelete(order.id)} disabled={!!editingOrder} />
                    )}
                  </td>
                </tr>
                );
              })
            )}
          </tbody>
        </table>
        <div className={styles.tableFooter}>
          
        </div>
      </div>
    </div>
  );
};

export default OrdersTable;